/**
 * Mock data for the YaaS Agents page — the agents being incentivized
 * across the live incentive programs (Use Case 1, Steps 2–3: agent action +
 * attribution). Each row makes attribution legible: an agent's wallet, an
 * approved corridor, settled volume against the threshold, and the weekend
 * bonus condition. Agents carry an explicit `qualifiedProgramIds` link to the
 * programs they qualify for, so the master-detail pane can render the
 * "Programs qualified for" relationship. All amounts are USDPT.
 */

import { type Program, PROGRAMS } from "../shared/programMocks";

export type WalletStatus = "activated" | "not_activated";

/**
 * Attribution outcome — whether the agent's settlement activity
 * qualifies for program rewards.
 */
export type EligibilityStatus = "threshold_met" | "below_threshold" | "not_enrolled";

export interface Agent {
    id: string;
    name: string;
    corridor: string;
    walletStatus: WalletStatus;
    /** USDPT settled in the current program period. */
    settled: number;
    /** Count of weekend / after-hours settlements (bonus condition). */
    weekendSettlements: number;
    /** Rewards earned so far this period, in USDPT. */
    rewards: number;
    eligibility: EligibilityStatus;
    /** Ids of the programs this agent qualifies for (cross-domain link). */
    qualifiedProgramIds: string[];
}

/** The settlement threshold (USDPT) the program rewards crossing. */
export const SETTLEMENT_THRESHOLD = 2000;

const PROGRAM_PHILIPPINES = "agent-usdpt-settlement-accelerator";
const PROGRAM_BOLIVIA = "bolivia-agent-wallet-activation-drive";
const PROGRAM_US = "us-usdpt-settlement-rebate";
const PROGRAM_BOLIVIA_ENDED = "bolivia-q1-remittance-rewards";

// ─── Bulk agent generation ─────────────────────────────────────────────
// Deterministic filler agents so the live + ended programs have realistic
// rosters. Names are built from corridor-specific place + suffix pools that
// exclude the curated agents' places, so generated names never collide.
const CORRIDOR_LABEL: Record<string, string> = {
    "united-states": "United States",
    bolivia: "Bolivia",
    mexico: "Mexico"
};

const GEN_NAME_POOLS: Record<string, { places: string[]; suffixes: string[] }> = {
    "united-states": {
        places: [
            "El Paso",
            "San Antonio",
            "Laredo",
            "McAllen",
            "Brownsville",
            "Tucson",
            "Phoenix",
            "Fresno",
            "Bakersfield",
            "Stockton",
            "Santa Ana",
            "Oakland"
        ],
        suffixes: ["Cash", "Transfers", "Wire", "Money", "Send", "Remit"]
    },
    bolivia: {
        places: [
            "Sucre",
            "Oruro",
            "Tarija",
            "Potosí",
            "El Alto",
            "Montero",
            "Trinidad",
            "Riberalta",
            "Yacuiba",
            "Quillacollo",
            "Sacaba",
            "Camiri"
        ],
        suffixes: ["Cambio", "Envíos", "Giros", "Remesas", "Money", "Express"]
    },
    mexico: {
        places: ["Querétaro", "Cancún", "Toluca", "Morelia", "Culiacán", "Hermosillo"],
        suffixes: ["Money", "Envíos", "Cambio", "Giros"]
    }
};

function genName(corridor: string, n: number): string {
    const pool = GEN_NAME_POOLS[corridor] ?? GEN_NAME_POOLS.mexico;
    const place = pool.places[n % pool.places.length];
    const suffix = pool.suffixes[Math.floor(n / pool.places.length) % pool.suffixes.length];
    return `${place} ${suffix}`;
}

/** Build the bulk filler agents distributed across the live + ended programs. */
function generateAgents(): Agent[] {
    const plan: {
        count: number;
        corridor: string;
        programIds: string[];
        enrolled: boolean;
    }[] = [
        { count: 22, corridor: "united-states", programIds: [PROGRAM_US], enrolled: true },
        { count: 14, corridor: "bolivia", programIds: [PROGRAM_BOLIVIA], enrolled: true },
        { count: 9, corridor: "bolivia", programIds: [PROGRAM_BOLIVIA_ENDED], enrolled: true },
        { count: 5, corridor: "mexico", programIds: [], enrolled: false }
    ];

    const agents: Agent[] = [];
    const nameCounter: Record<string, number> = {};
    let idx = 0;

    for (const group of plan) {
        for (let i = 0; i < group.count; i++) {
            const n = nameCounter[group.corridor] ?? 0;
            nameCounter[group.corridor] = n + 1;
            const settled = group.enrolled ? (600 + ((idx * 1373) % 23000)) * 100 : 0;
            agents.push({
                id: String(7100 + idx),
                name: genName(group.corridor, n),
                corridor: CORRIDOR_LABEL[group.corridor] ?? group.corridor,
                walletStatus: group.enrolled ? "activated" : "not_activated",
                settled,
                weekendSettlements: group.enrolled ? idx % 7 : 0,
                rewards: group.enrolled ? Math.round(settled * 0.016 * 100) / 100 : 0,
                eligibility: !group.enrolled
                    ? "not_enrolled"
                    : settled >= SETTLEMENT_THRESHOLD
                      ? "threshold_met"
                      : "below_threshold",
                qualifiedProgramIds: group.programIds
            });
            idx += 1;
        }
    }
    return agents;
}

const CURATED_AGENTS: Agent[] = [
    {
        id: "4471",
        name: "Aurora Remit",
        corridor: "Philippines",
        walletStatus: "activated",
        settled: 8420,
        weekendSettlements: 6,
        rewards: 142.1,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "3120",
        name: "Cebu Express",
        corridor: "Philippines",
        walletStatus: "activated",
        settled: 5310,
        weekendSettlements: 3,
        rewards: 79.65,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "2208",
        name: "Manila Central",
        corridor: "Philippines",
        walletStatus: "activated",
        settled: 12940,
        weekendSettlements: 9,
        rewards: 226.45,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "5093",
        name: "Davao Quik",
        corridor: "Philippines",
        walletStatus: "activated",
        settled: 1850,
        weekendSettlements: 2,
        rewards: 34.25,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "6640",
        name: "Iloilo Send",
        corridor: "Philippines",
        walletStatus: "activated",
        settled: 640,
        weekendSettlements: 0,
        rewards: 25,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "9301",
        name: "La Paz Cambio",
        corridor: "Bolivia",
        walletStatus: "activated",
        settled: 6120,
        weekendSettlements: 2,
        rewards: 98.4,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_BOLIVIA]
    },
    {
        id: "9355",
        name: "Santa Cruz Envíos",
        corridor: "Bolivia",
        walletStatus: "activated",
        settled: 3480,
        weekendSettlements: 1,
        rewards: 61.2,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_BOLIVIA]
    },
    {
        id: "9412",
        name: "Cochabamba Giros",
        corridor: "Bolivia",
        walletStatus: "activated",
        settled: 1240,
        weekendSettlements: 0,
        rewards: 50,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_BOLIVIA]
    },
    {
        id: "5520",
        name: "Rio Grande Cash",
        corridor: "United States",
        walletStatus: "activated",
        settled: 18750,
        weekendSettlements: 5,
        rewards: 281.25,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_US]
    },
    {
        id: "5588",
        name: "Lone Star Transfers",
        corridor: "United States",
        walletStatus: "activated",
        settled: 9640,
        weekendSettlements: 3,
        rewards: 144.6,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_US]
    },
    {
        id: "5601",
        name: "Bay Area Remesas",
        corridor: "United States",
        walletStatus: "activated",
        settled: 4200,
        weekendSettlements: 0,
        rewards: 63,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_US]
    },
    {
        id: "1187",
        name: "Guadalajara Money",
        corridor: "Mexico",
        walletStatus: "activated",
        settled: 4180,
        weekendSettlements: 1,
        rewards: 62.7,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    },
    {
        id: "0912",
        name: "Mumbai Transfers",
        corridor: "India",
        walletStatus: "activated",
        settled: 3025,
        weekendSettlements: 4,
        rewards: 51.4,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    },
    {
        id: "7714",
        name: "Bacolod Cash",
        corridor: "Philippines",
        walletStatus: "not_activated",
        settled: 0,
        weekendSettlements: 0,
        rewards: 0,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    },
    {
        id: "8852",
        name: "Monterrey Send",
        corridor: "Mexico",
        walletStatus: "not_activated",
        settled: 0,
        weekendSettlements: 0,
        rewards: 0,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    }
];

/** Settlement volumes are quarterly agent throughput — scaled up so reward
 *  math (≈1.6% of settled) lands at program-budget magnitudes. */
const SETTLED_SCALE = 100;

/** Curated, hand-tuned agents (scaled) first; then ~50 generated fillers. */
export const AGENTS: Agent[] = [
    ...CURATED_AGENTS.map((a) => ({
        ...a,
        settled: a.settled * SETTLED_SCALE,
        rewards: Math.round(a.rewards * SETTLED_SCALE * 100) / 100
    })),
    ...generateAgents()
];

/** Programs a given agent qualifies for (cross-domain link). */
export function programsForAgent(agent: Agent): Program[] {
    return PROGRAMS.filter((program) => agent.qualifiedProgramIds.includes(program.id));
}

/** Programs the agent has participated in that the detail pane surfaces —
 *  live (active) and completed (ended), but not unlaunched drafts. */
export function activeProgramsForAgent(agent: Agent): Program[] {
    return programsForAgent(agent).filter((program) => program.status !== "in_progress");
}

// ─── Derived attribution figures (demo) ──────────────────────────────────
// These are computed from the seeded agent so the detail pane stays rich
// without bloating every mock entry.

// Base58 alphabet (Bitcoin/Solana) — no 0, O, I, or l.
const BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Deterministic base58 string of `length` chars seeded by `seed` (xorshift). */
function pseudoBase58(seed: string, length: number): string {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    let out = "";
    while (out.length < length) {
        h ^= h << 13;
        h >>>= 0;
        h ^= h >> 17;
        h ^= h << 5;
        h >>>= 0;
        out += BASE58[h % 58];
    }
    return out;
}

/** Deterministic pseudo wallet address for an agent (Solana base58, 44 chars). */
export function walletAddressOf(agent: Agent): string {
    return pseudoBase58(agent.id, 44);
}

/** Abbreviated wallet address for compact table cells, e.g. `7xKp..9fQr`. */
export function shortAddressOf(agent: Agent): string {
    const a = walletAddressOf(agent);
    return `${a.slice(0, 4)}..${a.slice(-4)}`;
}

/** Current USDPT holding — the balance the reward policies key off. */
export function balanceOf(agent: Agent): number {
    return Math.round(agent.settled * 0.6);
}

/** Total settlement count this period (weekend settlements are a subset). */
export function settlementsOf(agent: Agent): number {
    return Math.max(agent.weekendSettlements, Math.round(agent.settled / 700));
}

/** Rewards accrued toward the next distribution (a portion of the period). */
export function rewardsPendingOf(agent: Agent): number {
    return Math.round(agent.rewards * 0.4 * 100) / 100;
}

/** A single rewards-distribution transaction in the agent's history. */
export interface RewardPayment {
    date: string;
    programName: string;
    balance: number;
    rewards: number;
    /** Realized APY for this distribution. */
    apy: number;
    /** Base58 transaction signature — links to solscan.io/tx/<sig>. */
    txSignature: string;
}

// ─── Rewards-distribution history (weekly cadence) ────────────────────────
// The history is generated to span the program's actual runtime at a weekly
// cadence, and to *sum exactly* to the entity's lifetime `rewards` total —
// the source of truth consumed by the dashboard, programs, and reporting
// surfaces. Each row's APY is held near the program's base APY (with mild
// deterministic jitter), and each row's balance is back-computed from its
// rewards + APY so the row math (rewards ≈ balance × apy/100 × 7/365) holds.

const REWARD_CYCLE_DAYS = 7;
const MAX_REWARD_DISTRIBUTIONS = 26;

/** The most recent distribution date the history walks backwards from. */
const REWARD_ANCHOR = { year: 2025, month: 2, day: 23 }; // Mar 23 (0-based month)
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Format a Date as "MMM D" (e.g. "Mar 9"), matching the table style. */
function formatRewardDate(d: Date): string {
    return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** A deterministic float in [0, 1) seeded by a string (FNV-1a + xorshift). */
function seededUnit(seed: string): number {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    h ^= h << 13;
    h >>>= 0;
    h ^= h >> 17;
    h ^= h << 5;
    h >>>= 0;
    return (h % 100000) / 100000;
}

/** Number of weekly distributions a program of `daysElapsed` has run. */
function distributionCount(daysElapsed: number): number {
    const weeks = Math.floor(daysElapsed / REWARD_CYCLE_DAYS);
    return Math.max(1, Math.min(MAX_REWARD_DISTRIBUTIONS, weeks));
}

/** Rewards payment history for an agent — one row per weekly distribution
 *  cycle over the active program's runtime, oldest first. Sums exactly to
 *  the agent's lifetime `rewards`. Empty when the agent has no active
 *  program or no rewards yet. */
export function rewardPaymentsForAgent(agent: Agent): RewardPayment[] {
    const active = activeProgramsForAgent(agent);
    if (active.length === 0 || agent.rewards === 0) return [];
    const program = active[0];
    const baseApy = program.baseApy ?? (program.programType === "continuous" ? 6 : 8);
    const count = distributionCount(program.daysElapsed ?? 90);

    const anchor = Date.UTC(REWARD_ANCHOR.year, REWARD_ANCHOR.month, REWARD_ANCHOR.day);

    // Mild linear growth weights (earlier weeks slightly smaller), so the
    // split is realistic rather than flat — but still sums to the total.
    const weights: number[] = [];
    let weightTotal = 0;
    for (let i = 0; i < count; i++) {
        const w = count === 1 ? 1 : 0.8 + 0.4 * (i / (count - 1));
        weights.push(w);
        weightTotal += w;
    }

    const payments: RewardPayment[] = [];
    let allocated = 0;
    for (let i = 0; i < count; i++) {
        const rewards =
            i === count - 1
                ? Math.round((agent.rewards - allocated) * 100) / 100
                : Math.round(((agent.rewards * weights[i]) / weightTotal) * 100) / 100;
        allocated += rewards;

        // Per-cycle APY: base ± ~0.3, deterministic per agent + cycle.
        const apy = Math.round((baseApy + (seededUnit(`${agent.id}-apy-${i}`) - 0.5) * 0.6) * 100) / 100;
        // Back-compute the balance so the row math is self-consistent:
        // rewards ≈ balance × (apy/100) × (REWARD_CYCLE_DAYS/365).
        const balance = apy > 0 ? Math.round(rewards / ((apy / 100) * (REWARD_CYCLE_DAYS / 365))) : balanceOf(agent);

        // Walk backwards weekly from the anchor (oldest row first).
        const date = new Date(anchor - (count - 1 - i) * REWARD_CYCLE_DAYS * 86400000);

        payments.push({
            date: formatRewardDate(date),
            programName: program.name,
            balance,
            rewards,
            apy,
            txSignature: pseudoBase58(`${agent.id}-tx-${i}`, 88)
        });
    }
    return payments;
}

/** Agents who qualify for a given program (cross-domain link). */
export function agentsForProgram(programId: string): Agent[] {
    return AGENTS.filter((agent) => agent.qualifiedProgramIds.includes(programId));
}

/** Total USDPT settled by a program's agents (single source of truth). */
export function programAmountSettled(programId: string): number {
    return agentsForProgram(programId).reduce((sum, a) => sum + a.settled, 0);
}

/** Current USDPT TVL — total USDPT held by a program's agents. Derived from
 *  `balanceOf` so it reconciles with the issuer-wide `TOTAL_HELD`. */
export function programAmountHeld(programId: string): number {
    return Math.round(agentsForProgram(programId).reduce((sum, a) => sum + balanceOf(a), 0));
}

/** Total rewards a program has distributed so far = Σ its agents' rewards.
 *  Drives the program stats, budget-spent meter, and remaining-budget — so
 *  every surface agrees. */
export function programRewardsDistributed(programId: string): number {
    return agentsForProgram(programId).reduce((sum, a) => sum + a.rewards, 0);
}
